/**
 * Login (§18 /login, §19.1). Single path: Google Workspace SSO for
 * managers/admins (domain-restricted on the worker). The worker owns the OIDC
 * handshake and sets the HttpOnly cookie, then 302s back — so this is a real
 * full-page navigation, not fetch. The visual card is <LoginCard/> (shared with
 * the dev preview); this container handles the session redirect + ?error.
 */
import { Navigate, useSearchParams } from "react-router-dom";
import { apiBase } from "../lib/api.js";
import { useSession, homePathFor } from "../components/auth.js";
import { CairnMark, Icon } from "../components/ui/index.js";

/** Sanitize a redirect target to a same-app path (no protocol/host). */
function safeNext(raw: string | null): string {
  if (!raw) return "/queue";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/queue";
  return raw;
}

/** Google "G" mark (brand-correct four colours). */
function GoogleG() {
  return (
    <svg className="google-btn__g" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5Z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.1 5.3-4.6 7l7.2 5.6c4.2-3.9 6.6-9.6 6.6-17.1Z" />
      <path fill="#FBBC05" d="M10.4 28.3c-.5-1.4-.8-2.9-.8-4.3s.3-3 .8-4.3l-7.8-6.1C1 16.6 0 20.2 0 24s1 7.4 2.6 10.4l7.8-6.1Z" />
      <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.3-5.6l-7.2-5.6c-2 1.4-4.6 2.2-8.1 2.2-6.4 0-11.7-3.7-13.6-9.7l-7.8 6.1C6.5 42.6 14.6 48 24 48Z" />
    </svg>
  );
}

/** Presentational login card — reused by the dev preview. */
export function LoginCard({ error, onSignIn }: { error?: string | null; onSignIn: () => void }) {
  return (
    <div className="login__card">
      <div className="login__mark">
        <CairnMark size={30} />
      </div>
      <div>
        <h1 className="login__title">Cairn</h1>
        <p className="login__sub">Alpine Nation fulfilment</p>
      </div>

      {error === "forbidden" && (
        <p className="login__error" role="alert">
          <Icon name="alert" size={15} />
          <span>
            Your account isn’t allowed — sign in with an <strong>@alpinenation.com</strong> Workspace
            account.
          </span>
        </p>
      )}

      <button type="button" className="google-btn" onClick={onSignIn}>
        <GoogleG />
        Sign in with Google
      </button>

      <p className="login__hint">Managers and admins sign in with their Workspace account.</p>
    </div>
  );
}

export function Login() {
  const [params] = useSearchParams();
  const { user, loading } = useSession();
  const error = params.get("error");
  const next = safeNext(params.get("next"));

  if (!loading && user) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  const ssoLogin = () => {
    window.location.href = `${apiBase}/auth/google/login?redirect=${encodeURIComponent(next)}`;
  };

  return (
    <div className="login">
      <LoginCard error={error} onSignIn={ssoLogin} />
    </div>
  );
}
